import { tryLaunchHelperApp } from '@/config/helperPresence';
import { getHelperDownloadUrl } from '@/config/helperDownload';
import {
  fetchHelperHealth,
  resolveHelperConnectionState,
} from '@/features/serial/helperClient';
import { useHelperDialogStore } from '@/state/helperDialogStore';
import { useHelperStartDialogStore } from '@/state/helperStartDialogStore';
import { useSerialStore } from '@/state/serialStore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

export function HelperStartDialog() {
  const open = useHelperStartDialogStore((state) => state.open);
  const close = useHelperStartDialogStore((state) => state.close);
  const showDownloadDialog = useHelperDialogStore((state) => state.show);
  const refreshHelperStatus = useSerialStore((state) => state.refreshHelperStatus);
  const [checking, setChecking] = useState(false);

  const handleOpenHelper = useCallback(() => {
    tryLaunchHelperApp();
  }, []);

  const handleCheckAgain = useCallback(async () => {
    setChecking(true);
    try {
      const health = await fetchHelperHealth();
      const connection = resolveHelperConnectionState(health);
      if (connection !== 'offline') {
        await refreshHelperStatus();
        close();
        return;
      }
    } finally {
      setChecking(false);
    }
  }, [close, refreshHelperStatus]);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Start Skyrover Helper</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Skyrover Helper looks installed on this PC, but it is not running right now. Start it
          to enable upload and Board Manager.
        </Typography>

        <Box component="ol" sx={{ m: 0, pl: 2.5, color: 'text.secondary', fontSize: 14 }}>
          <li>Click <strong>Open Helper</strong> below, or open it from the Windows Start Menu</li>
          <li>Leave the helper running (port 7421)</li>
          <li>Click <strong>Check again</strong> — status bar should show <strong>Helper online</strong></li>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Never installed the helper on this PC?{' '}
          <Link
            component="button"
            type="button"
            onClick={() => {
              close();
              showDownloadDialog();
            }}
            sx={{ verticalAlign: 'baseline' }}
          >
            Download instead
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={close}>Later</Button>
        <Button
          onClick={() => void handleCheckAgain()}
          startIcon={<RefreshIcon />}
          disabled={checking}
        >
          {checking ? 'Checking…' : 'Check again'}
        </Button>
        <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={handleOpenHelper}>
          Open Helper
        </Button>
      </DialogActions>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          If Open Helper does nothing, install the latest helper:{' '}
          <Link href={getHelperDownloadUrl()} download>
            Download
          </Link>
        </Typography>
      </Box>
    </Dialog>
  );
}
